/**
 * Toast Notification Utility
 * Simple toast notification system for displaying temporary messages
 * Positioned at top-right for both desktop and mobile
 */

/**
 * Inject toast styles into the document if not already present
 * This approach doesn't violate CSP as it's not inline styles on elements
 */
function injectToastStyles() {
  // Check if styles are already injected
  if (document.getElementById('ottu-toast-styles')) {
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.id = 'ottu-toast-styles';
  styleTag.textContent = `
    .ottu__sdk-toast {
      position: fixed !important;
      top: 20px !important;
      right: 20px !important;
      padding: 12px 24px;
      border-radius: 8px;
      font-family: 'Poppins', sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #ffffff !important;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 999999 !important;
      opacity: 0;
      transform: translateX(400px);
      transition: opacity 0.3s ease, transform 0.3s ease;
      cursor: pointer;
      max-width: 90%;
      word-wrap: break-word;
      pointer-events: auto !important;
      visibility: visible !important;
    }

    .ottu__sdk-toast-success {
      background-color: #26AF61 !important;
    }

    .ottu__sdk-toast-error {
      background-color: #E74C3C !important;
    }

    .ottu__sdk-toast-show {
      opacity: 1 !important;
      transform: translateX(0) !important;
    }

    .ottu__sdk-toast-hide {
      opacity: 0 !important;
      transform: translateX(400px) !important;
    }

    @media (max-width: 768px) {
      .ottu__sdk-toast {
        top: 10px !important;
        right: 10px !important;
        left: 10px !important;
        max-width: calc(100% - 20px);
        transform: translateY(-100px) !important;
      }

      .ottu__sdk-toast-show {
        transform: translateY(0) !important;
      }

      .ottu__sdk-toast-hide {
        transform: translateY(-100px) !important;
      }
    }
  `;

  document.head.appendChild(styleTag);
}

/**
 * Show a toast notification
 * @param {string} message - Message to display in the toast
 * @param {Object} options - Toast configuration options
 * @param {string} options.type - Toast type ('success' or 'error'), defaults to 'success'
 * @param {number} options.duration - Duration in milliseconds before auto-hide, defaults to 3000
 */
exports.showToast = function(message, options = {}) {
  // Inject styles first
  injectToastStyles();

  const {
    type = 'success',
    duration = 3000
  } = options;

  // Remove any existing toast first
  const existingToast = document.querySelector('.ottu__sdk-toast');
  if (existingToast) {
    existingToast.remove();
  }

  // Create toast element
  const toast = document.createElement('div');
  toast.className = `ottu__sdk-toast ottu__sdk-toast-${type}`;
  toast.textContent = message;
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'polite');

  // Add to DOM
  document.body.appendChild(toast);

  // Trigger animation by adding show class after a small delay
  setTimeout(() => {
    toast.classList.add('ottu__sdk-toast-show');
  }, 10);

  // Auto-hide after duration
  const hideTimeout = setTimeout(() => {
    hideToast(toast);
  }, duration);

  // Allow manual dismiss on click
  toast.addEventListener('click', () => {
    clearTimeout(hideTimeout);
    hideToast(toast);
  });
};

/**
 * Hide and remove a toast element
 * @param {HTMLElement} toast - Toast element to hide
 */
function hideToast(toast) {
  if (!toast) {
    return;
  }

  toast.classList.remove('ottu__sdk-toast-show');
  toast.classList.add('ottu__sdk-toast-hide');

  // Remove from DOM after animation completes
  setTimeout(() => {
    if (toast && toast.parentNode) {
      toast.remove();
    }
  }, 300);
}

/**
 * Show a success toast
 * @param {string} message - Success message to display
 * @param {Object} options - Additional options (duration)
 */
exports.showSuccessToast = function(message, options = {}) {
  exports.showToast(message, { ...options, type: 'success' });
};

/**
 * Show an error toast
 * @param {string} message - Error message to display
 * @param {Object} options - Additional options (duration)
 */
exports.showErrorToast = function(message, options = {}) {
  exports.showToast(message, { ...options, type: 'error' });
};
